<template>
    <th
        scope="col"
        :class="classObject"
        data-spec-class="table-cell-header"
        :data-spec-id="attributeName ? `table-cell-header--${attributeName}` : UNSORTED"
        :data-spec-value="currentSortDirection"
        @click="onSortingClick"
    >
        <slot />

        <ChevronDownIcon
            :class="iconClassObject"
            aria-hidden="true"
            :style="iconStyleObject"
        />
    </th>
</template>

<script setup lang="ts">
import { withDefaults, defineEmits, defineProps, computed, ref, watch } from 'vue';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import { TableCellType, SortDirection } from '@/types';

interface Props {
    type?: TableCellType;
    isSortable?: boolean;
    attributeName?: string;
    sortField?: string;
    sorting?: string;
}

const props = withDefaults(defineProps<Props>(), {
    type: TableCellType.Normal,
    isSortable: false,
    attributeName: undefined,
    sortField: undefined,
    sorting: undefined,
});

const UNSORTED = 'unsorted';

const emits = defineEmits(['update:page-sorting']);

const sortFieldName = computed(() => props.sortField || props.attributeName);

function initialSortDirection() {
    if (props.sorting === `${sortFieldName.value}:asc`) {
        return SortDirection.Asc;
    }

    if (props.sorting === `${sortFieldName.value}:desc`) {
        return SortDirection.Desc;
    }

    return UNSORTED;
}

const currentSortDirection = ref(initialSortDirection());

const iconRotating = ref(currentSortDirection.value === SortDirection.Desc ? 180 : 0);

const isUnsorted = computed(() => currentSortDirection.value === UNSORTED);
const isSorted = computed(() => !isUnsorted.value);
const isSortableCondition = computed(() => {
    return props.isSortable && props.attributeName;
});

const classObject = computed(() => ({
    'py-1.5 px-6 pr-1 font-medium whitespace-nowrap transition duration-300 ease-in-out': true,
    'cursor-pointer group hover:text-secondary hover:font-bold': isSortableCondition.value,
    'text-left text-tertiary': props.type === TableCellType.Normal,
    '!font-bold text-secondary': props.type === TableCellType.Normal && isSorted.value,
    relative: props.type === TableCellType.Actions,
}));

const iconClassObject = computed(() => ({
    'h-5 w-5 inline ml-2': true,
    'group-hover:opacity-100 opacity-0 transition duration-300 ease-in-out': isUnsorted.value,
}));

const iconStyleObject = computed(() => ({
    transform: `rotate(${iconRotating.value}deg)`,
    transition: 'transform 150ms ease',
}));

function onSortingClick() {
    if (!isSortableCondition.value) {
        return;
    }

    if (currentSortDirection.value === UNSORTED) {
        currentSortDirection.value = SortDirection.Asc;
        iconRotating.value = 0;
    } else if (currentSortDirection.value === SortDirection.Asc) {
        currentSortDirection.value = SortDirection.Desc;
        iconRotating.value = 180;
    } else if (currentSortDirection.value === SortDirection.Desc) {
        currentSortDirection.value = UNSORTED;
        iconRotating.value = 0;
    }
    const sortDirection = currentSortDirection.value === UNSORTED ? '' : currentSortDirection.value;
    emits('update:page-sorting', { attributeName: sortFieldName.value, sortDirection });
}

watch(
    () => props.sorting,
    (newSorting) => {
        if (!newSorting) {
            return;
        }
        const newAttributeName = newSorting.split(':')[0];
        if (newAttributeName && newAttributeName !== sortFieldName.value) {
            currentSortDirection.value = UNSORTED;
        }
        if (
            newAttributeName &&
            newAttributeName === sortFieldName.value &&
            currentSortDirection.value === ''
        ) {
            currentSortDirection.value = initialSortDirection();
        }
    }
);
</script>
